import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FaGithub, FaLinkedin, FaEnvelope, FaBars, FaTimes, FaDownload } from 'react-icons/fa'
import toast from 'react-hot-toast'
import {
  siteInfo,
  aboutParagraphs,
  navSections,
  experience,
  featuredProjects,
  additionalProjects,
  skillGroups,
  certifications,
  education,
  type Project,
  type NavSection,
} from './data/portfolio'

function ProjectCard({ project, compact = false }: { project: Project; compact?: boolean }) {
  const titleContent = project.link ? (
    <a
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-accent-light transition-colors"
    >
      {project.title}
    </a>
  ) : (
    project.title
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`section-card ${compact ? 'p-5' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="badge">{project.category}</span>
        {project.badges?.map((badge) => (
          <span key={badge} className="badge-highlight">
            {badge}
          </span>
        ))}
      </div>
      <h3 className={`font-display font-bold text-slate-50 mb-2 ${compact ? 'text-lg' : 'text-xl'}`}>
        {titleContent}
      </h3>
      {project.impact && (
        <p className="text-accent-light text-sm font-medium mb-2">{project.impact}</p>
      )}
      <p className={`text-slate-300 mb-4 ${compact ? 'text-sm' : ''}`}>{project.description}</p>
      <div className="flex flex-wrap gap-2">
        {project.tech.map((tech) => (
          <span key={tech} className="tech-tag text-xs">
            {tech}
          </span>
        ))}
      </div>
    </motion.div>
  )
}

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState<NavSection>('home')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const windowHeight = window.innerHeight
      const newActiveSection = navSections.find((section) => {
        const el = document.getElementById(section)
        if (el) {
          const offsetTop = el.offsetTop
          const offsetBottom = offsetTop + el.offsetHeight
          return (
            currentScrollY >= offsetTop - windowHeight / 2 &&
            currentScrollY < offsetBottom - windowHeight / 2
          )
        }
        return false
      })
      if (newActiveSection) {
        setActiveSection(newActiveSection)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollTo = (id: NavSection): void => {
    setMobileMenuOpen(false)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const copyEmail = () => {
    navigator.clipboard.writeText(siteInfo.email)
    toast.success('Email copied to clipboard!', {
      duration: 2000,
      position: 'bottom-center',
      style: { background: '#243044', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.08)' },
    })
  }

  return (
    <div className="bg-surface text-slate-300 min-h-screen">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-surface-card/80 backdrop-blur-md border-b border-white/[0.07]">
        <div className="max-w-content mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => scrollTo('home')}
              className="font-display font-semibold text-base md:text-lg text-slate-100 hover:text-accent-light transition-colors"
            >
              {siteInfo.name}
            </button>

            <ul className="hidden md:flex gap-6">
              {navSections.map((section) => (
                <li key={section}>
                  <button
                    onClick={() => scrollTo(section)}
                    className={`capitalize text-sm transition-colors ${
                      activeSection === section
                        ? 'text-accent-light font-semibold'
                        : 'text-slate-400 hover:text-slate-100'
                    }`}
                  >
                    {section}
                  </button>
                </li>
              ))}
            </ul>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-xl p-1 text-slate-400 hover:text-slate-100"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>

          {mobileMenuOpen && (
            <ul className="md:hidden pb-4 space-y-3 border-t border-white/[0.07] pt-4">
              {navSections.map((section) => (
                <li key={section}>
                  <button
                    onClick={() => scrollTo(section)}
                    className={`capitalize w-full text-left py-1 ${
                      activeSection === section
                        ? 'text-accent-light font-semibold'
                        : 'text-slate-400 hover:text-slate-100'
                    }`}
                  >
                    {section}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </nav>

      <main className="max-w-content mx-auto px-4 pt-24 pb-16">
        <section
          id="home"
          className="min-h-[90dvh] flex items-center justify-center hero-bg rounded-2xl px-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl"
          >
            <p className="text-accent-light text-sm font-medium tracking-wide uppercase mb-4">
              {siteInfo.location}
            </p>
            <h1 className="font-display text-5xl md:text-6xl font-bold mb-4 tracking-tight text-balance text-slate-50">
              {siteInfo.name}
            </h1>
            <p className="text-xl md:text-2xl text-slate-200 mb-2 font-display">
              {siteInfo.title}
            </p>
            <p className="text-slate-400 mb-4">{siteInfo.subtitle}</p>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed text-balance">
              {siteInfo.tagline}
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <button onClick={() => scrollTo('projects')} className="btn-primary">
                View Projects
              </button>
              <a href={siteInfo.resumeUrl} download className="btn-secondary inline-flex items-center gap-2">
                <FaDownload className="text-sm" />
                Download Resume
              </a>
            </div>

            <div className="flex justify-center gap-5 text-2xl">
              <a
                href={siteInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-accent-light transition-colors"
                aria-label="GitHub"
              >
                <FaGithub />
              </a>
              <a
                href={siteInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-accent-light transition-colors"
                aria-label="LinkedIn"
              >
                <FaLinkedin />
              </a>
              <button
                onClick={copyEmail}
                className="text-slate-500 hover:text-accent-light transition-colors"
                aria-label="Email"
              >
                <FaEnvelope />
              </button>
            </div>
          </motion.div>
        </section>

        <section id="about" className="py-24 about-bg rounded-2xl my-12 px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="section-heading">About Me</h2>
            <div className="space-y-4 text-slate-300 leading-relaxed">
              {aboutParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </motion.div>
        </section>

        <section id="experience" className="py-24">
          <h2 className="section-heading">Experience</h2>
          <p className="section-subheading">Research and industry roles building AI at scale.</p>
          <div className="space-y-6">
            {experience.map((job, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="section-card"
              >
                <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                  <div>
                    <h3 className="text-xl font-display font-bold text-slate-50">{job.title}</h3>
                    <p className="text-accent-light font-medium">{job.company}</p>
                  </div>
                  <p className="text-slate-500 text-sm whitespace-nowrap">{job.date}</p>
                </div>
                {job.highlights && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {job.highlights.map((highlight) => (
                      <span key={highlight} className="badge-highlight">
                        {highlight}
                      </span>
                    ))}
                  </div>
                )}
                <ul className="space-y-2 text-slate-300">
                  {job.description.map((item, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-accent mt-1.5 shrink-0">▸</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="projects" className="py-24">
          <h2 className="section-heading">Featured Work</h2>
          <p className="section-subheading">
            Healthcare AI research and production systems from clinical deployment.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            {featuredProjects.map((project) => (
              <ProjectCard key={project.title} project={project} />
            ))}
          </div>

          <h3 className="font-display text-2xl font-bold text-slate-50 mb-2">Additional Projects</h3>
          <p className="text-slate-400 mb-6">Selected full-stack and research work.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {additionalProjects.map((project) => (
              <ProjectCard key={project.title} project={project} compact />
            ))}
          </div>
        </section>

        <section id="skills" className="py-24">
          <h2 className="section-heading">Skills</h2>
          <p className="section-subheading">Generative AI, machine learning, and production engineering.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {skillGroups.map((group) => (
              <motion.div
                key={group.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="section-card"
              >
                <h3 className="font-display font-semibold text-accent-light mb-3">{group.label}</h3>
                <div className="flex flex-wrap gap-2">
                  {group.skills.map((skill) => (
                    <span key={skill} className="tech-tag text-xs">
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div>
            <h3 className="font-display font-semibold text-lg text-slate-50 mb-4">Certifications</h3>
            <div className="flex flex-wrap gap-3">
              {certifications.map((cert) => (
                <span key={cert} className="badge-highlight text-sm px-3 py-1">
                  {cert}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section id="education" className="py-24">
          <h2 className="section-heading">Education</h2>
          <p className="section-subheading">Graduate and undergraduate training in AI and computer science.</p>
          <div className="space-y-6">
            {education.map((edu, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="section-card"
              >
                <h3 className="text-xl font-display font-bold text-slate-50 mb-1">{edu.degree}</h3>
                <p className="text-accent-light font-medium mb-1">{edu.school}</p>
                <p className="text-slate-500 text-sm mb-4">{edu.date}</p>
                {edu.details && (
                  <div className="space-y-1 text-slate-300 mb-4">
                    {edu.details.map((detail) => (
                      <p key={detail}>{detail}</p>
                    ))}
                  </div>
                )}
                {edu.coursework && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                      Relevant Coursework
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {edu.coursework.map((course) => (
                        <span key={course} className="badge">
                          {course}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        <section id="contact" className="py-24 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-lg"
          >
            <h2 className="section-heading">Get In Touch</h2>
            <p className="text-slate-400 mb-2">{siteInfo.location}</p>
            <p className="text-slate-300 mb-8">
              Open to research collaborations, AI engineering roles, and opportunities in healthcare technology.
            </p>
            <button
              onClick={copyEmail}
              className="text-accent-light hover:text-accent text-lg mb-6 cursor-pointer transition-colors"
            >
              {siteInfo.email}
            </button>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={`mailto:${siteInfo.email}`} className="btn-primary">
                Email Me
              </a>
              <a href={siteInfo.resumeUrl} download className="btn-secondary inline-flex items-center gap-2">
                <FaDownload className="text-sm" />
                Resume
              </a>
            </div>
            <div className="flex justify-center gap-5 mt-8 text-xl">
              <a
                href={siteInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-accent-light transition-colors"
              >
                <FaGithub />
              </a>
              <a
                href={siteInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-accent-light transition-colors"
              >
                <FaLinkedin />
              </a>
            </div>
          </motion.div>
        </section>
      </main>
    </div>
  )
}
